import React, { Suspense, lazy, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { Box, Chip, CircularProgress, Dialog, IconButton, TextField, Tooltip, Typography } from '@material-ui/core'
import { Add as AddIcon, Close as CloseIcon } from '@material-ui/icons'
import ResultButton from 'src/views/FileUploadView/components/ResultButton'
import { getHPOFromClinicalHistory } from 'src/api/HPO_suggestion/hpo_suggestion'
import { GroupLabel, MONO_SX, Muted, TextLink } from './ui'
import { fromTerms, toTerms } from '../model/hpo'

// HPO terms suggested from a sample's clinical history, under the Clinical history
// field, as in the old start wizard. Once the text settles the terms show as chips:
// a click adds one to the sample's HPO terms, the x dismisses it for this sample.
// "View graph" opens the clusters beside the clinical history, which can be edited
// there; Cancel puts the sample's terms back as they were, "Save & close" keeps
// them. Nothing is added by itself and nothing here holds back a start.

// Loaded on first use: the graph brings its layout library with it.
const HPOClusterGraph = lazy(() => import('src/views/AnalysisView/AnnotationView/components/HPOClusterGraph'))

export const SETTLE_MS = 800
// At most this many chips, as in the old wizard.
export const MAX_CHIPS = 99
const CACHE_SIZE = 50

// Answers by trimmed clinical history, shared by every editor, so reopening a row
// or moving between rows with the same text never asks again. A request on its
// way is shared too.
const cache = new Map()
const inFlight = new Map()
// Dismissed HP ids by sample, for as long as the page is open.
const dismissedBySample = new Map()
const listeners = new Set()
const NONE = new Set()

// For tests.
export const resetHpoSuggestions = () => {
  cache.clear()
  inFlight.clear()
  dismissedBySample.clear()
}

const synonymsOf = (text) => {
  const line = text.match(/^Synonyms: (.+)$/m)?.[1]
  return line
    ? line
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : []
}

// The endpoint returns each term as its HPO document
// ("ID: HP:...\nName: ...\nDefinition: ...\nSynonyms: a, b").
export const parseSuggestions = (docs = [], graph = null) => {
  const nodes = new Map(((graph && graph.nodes) || []).map((n) => [n.id, n]))
  const seen = new Set()
  const out = []
  ;(docs || []).forEach((doc) => {
    const text = String(doc || '')
    const id = text.match(/HP:\d+/)?.[0]
    if (!id || seen.has(id)) return
    seen.add(id)
    const node = nodes.get(id)
    const name = text.match(/Name: ([^\n]+)/)?.[1]?.trim() || node?.name || id
    const synonyms = node?.synonyms?.length ? node.synonyms : synonymsOf(text)
    out.push({ id, name, synonyms })
  })
  return out
}

// A suggestion as the HPO field's own term object (see model/hpo.js).
const termOf = ({ id, name, synonyms }) => ({
  value: id,
  label: `${name} (${id})`,
  name,
  hpo_id: id,
  synonyms: synonyms || [],
})

const requestSuggestions = (key) => {
  if (cache.has(key)) return Promise.resolve(cache.get(key))
  if (!inFlight.has(key)) {
    const request = getHPOFromClinicalHistory(key)
      .then((data) => {
        if (!data || !data.success) throw new Error((data && data.error) || 'No HPO suggestions')
        const graph =
          data.graph && data.graph.nodes && data.graph.nodes.length
            ? { ...data.graph, edges: data.graph.edges || [] }
            : null
        const result = { terms: parseSuggestions(data.hpo_names, graph), graph }
        cache.set(key, result)
        if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value)
        return result
      })
      .finally(() => inFlight.delete(key))
    inFlight.set(key, request)
  }
  return inFlight.get(key)
}

/**
 * Asks for the suggestions of a clinical history typed where none show (the AI
 * table's history cell), so they are ready when the sample's HPO terms open, as
 * the old wizard asked while the text was typed.
 */
export const prefetchHpoSuggestions = (text) => {
  const key = String(text || '').trim()
  if (!key) return Promise.resolve(null)
  return requestSuggestions(key).catch(() => null)
}

const dismissedOf = (scope) => dismissedBySample.get(scope) || NONE

const putDismissed = (scope, next) => {
  dismissedBySample.set(scope, next)
  listeners.forEach((listener) => listener(scope))
}

const setDismissed = (scope, ids, dismissed) => {
  const next = new Set(dismissedOf(scope))
  ids.forEach((id) => (dismissed ? next.add(id) : next.delete(id)))
  putDismissed(scope, next)
}

// The sample's dismissed ids; every open editor of the sample follows them.
const useDismissed = (scope) => {
  const [, rerender] = useReducer((n) => n + 1, 0)
  useEffect(() => {
    const listener = (changed) => {
      if (changed === scope) rerender()
    }
    listeners.add(listener)
    return () => listeners.delete(listener)
  }, [scope])
  return dismissedOf(scope)
}

const IDLE = { status: 'idle', result: null }

// The suggestions for a clinical history: at once when the editor opens on a
// sample's text, then each time the text settles. Any text that is not blank is
// asked for, as in the old wizard. `result` is the last answer for the sample, kept
// on screen while a newer text is on its way; answers for an older text are ignored.
const useSuggestions = (text, scope) => {
  const key = String(text || '').trim()
  const [state, setState] = useState(() => (key && cache.has(key) ? { status: 'done', result: cache.get(key) } : IDLE))
  const seq = useRef(0)
  const mounted = useRef(false)
  // The sample the editor last showed: another sample is a fresh open.
  const opened = useRef(null)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback((k) => {
    seq.current += 1
    const id = seq.current
    setState((prev) => ({ status: 'loading', result: prev.result }))
    requestSuggestions(k).then(
      (result) => {
        if (mounted.current && id === seq.current) setState({ status: 'done', result })
      },
      () => {
        if (mounted.current && id === seq.current) setState((prev) => ({ status: 'error', result: prev.result }))
      },
    )
  }, [])

  useEffect(() => {
    const opening = !opened.current || opened.current.scope !== scope
    opened.current = { scope }
    seq.current += 1
    if (!key) {
      setState(IDLE)
      return undefined
    }
    if (cache.has(key)) {
      setState({ status: 'done', result: cache.get(key) })
      return undefined
    }
    // Another sample's answer is not kept on screen.
    setState((prev) =>
      opening ? IDLE : prev.status === 'done' || prev.status === 'idle' ? prev : { ...prev, status: 'idle' },
    )
    if (opening || inFlight.has(key)) {
      load(key)
      return undefined
    }
    const timer = setTimeout(() => load(key), SETTLE_MS)
    return () => clearTimeout(timer)
  }, [key, scope, load])

  return { ...state, retry: () => load(key) }
}

const Spinner = () => <CircularProgress size={12} thickness={5} color="inherit" sx={{ flexShrink: 0 }} />

const Finding = ({ sx }) => (
  <Muted sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ...sx }}>
    <Spinner />
    Finding HPO terms…
  </Muted>
)

const CouldNot = ({ onRetry, sx }) => (
  <Muted sx={sx}>
    Could not suggest HPO terms. <TextLink onClick={onRetry}>Try again</TextLink>
  </Muted>
)

const idsOf = (hpo) => (hpo || []).map((item) => item.id).join(',')

// The clusters beside the sample's clinical history, which can be edited here: the
// graph follows the text as it settles. The ✓ and ✗ on a term apply at once;
// Cancel (and the x, Esc or a click outside) puts the sample's terms and dismissals
// back as they were when the graph opened, "Save & close" keeps them. Edits to the
// clinical history stay either way.
const ClustersDialog = ({
  label,
  text,
  onTextChange,
  status,
  retry,
  graph,
  count,
  selectedIds,
  dismissed,
  actions,
  onCancel,
  onSave,
}) => {
  const blank = !String(text || '').trim()
  return (
    <Dialog
      open
      onClose={onCancel}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { borderRadius: '12px', height: 'calc(100% - 64px)', m: 2 } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, px: 3, pt: 2.5, pb: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Suggested HPO clusters</Typography>
        {label && (
          <Typography component="span" noWrap sx={{ ...MONO_SX, color: 'text.secondary', minWidth: 0 }}>
            {label}
          </Typography>
        )}
        <Tooltip title="Discard changes">
          <IconButton
            onClick={onCancel}
            aria-label="Discard changes"
            size="small"
            sx={{ ml: 'auto', alignSelf: 'center' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2.5,
          px: 3,
          overflowY: { xs: 'auto', md: 'visible' },
        }}
      >
        <Box sx={{ width: { md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupLabel sx={{ mb: 0 }}>Clinical history</GroupLabel>
            {status === 'loading' && (
              <Box component="span" sx={{ display: 'flex', color: 'text.secondary' }}>
                <Spinner />
              </Box>
            )}
          </Box>
          <TextField
            multiline
            minRows={6}
            maxRows={24}
            size="small"
            value={text || ''}
            onChange={(e) => onTextChange && onTextChange(e.target.value)}
            placeholder="Symptoms, family history, previous tests"
            inputProps={{ 'aria-label': 'Clinical history', readOnly: !onTextChange }}
            sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 14.5, lineHeight: 1.5 } }}
          />
          <Muted>The graph updates as you type.</Muted>
          {status === 'error' && <CouldNot onRetry={retry} />}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: { xs: 360, md: 0 } }}>
          {graph ? (
            <Suspense fallback={<Muted>Loading…</Muted>}>
              <HPOClusterGraph
                graph={graph}
                onAccept={actions.accept}
                onReject={actions.reject}
                onRemove={actions.remove}
                onRestore={actions.restore}
                selectedIds={selectedIds}
                rejectedIds={dismissed}
                height="100%"
              />
            </Suspense>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {status === 'loading' ? (
                <Finding />
              ) : (
                <Muted>
                  {blank
                    ? 'Write the clinical history to see suggested terms.'
                    : 'No HPO terms suggested for this text.'}
                </Muted>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2 }}>
        <Muted sx={{ mr: 'auto' }}>
          {count === 0
            ? 'No HPO terms yet. Click ✓ on the terms you want to add.'
            : count === 1
            ? '1 HPO term on this sample'
            : `${count} HPO terms on this sample`}
        </Muted>
        <ResultButton onClick={onCancel} sx={{ height: 36, px: 2, fontSize: 14 }}>
          Cancel
        </ResultButton>
        <ResultButton primary onClick={onSave} sx={{ height: 36, px: 2, fontSize: 14 }}>
          Save &amp; close
        </ResultButton>
      </Box>
    </Dialog>
  )
}

/**
 * text: the clinical history; onTextChange(text) writes it back (from the graph);
 * hpo: the sample's HPO terms ({ id, name, term }); onChange(hpo) writes them back;
 * scope: the sample's uid, for its dismissals; label: the sample's name.
 */
const HpoSuggestions = ({ text, onTextChange, hpo, onChange, scope, label, sx }) => {
  const { status, result, retry } = useSuggestions(text, scope)
  const dismissed = useDismissed(scope)
  const [graphOpen, setGraphOpen] = useState(false)
  // The sample's terms and dismissals when the graph opened, for Cancel.
  const snapshot = useRef(null)
  const current = useMemo(() => toTerms(hpo || []), [hpo])
  const selectedIds = useMemo(() => new Set(current.map((t) => t.hpo_id || t.value)), [current])
  const suggested = useMemo(
    () => (result ? result.terms.filter((t) => !selectedIds.has(t.id)) : []),
    [result, selectedIds],
  )
  const terms = useMemo(() => suggested.filter((t) => !dismissed.has(t.id)).slice(0, MAX_CHIPS), [suggested, dismissed])
  const dismissedIds = useMemo(
    () => suggested.filter((t) => dismissed.has(t.id)).map((t) => t.id),
    [suggested, dismissed],
  )
  const graph = result && result.graph

  // Written the way the HPO field writes them: the whole list through fromTerms.
  const write = (next) => onChange(fromTerms(next))
  const add = (items) => {
    const undismiss = items.filter((t) => dismissed.has(t.id)).map((t) => t.id)
    if (undismiss.length) setDismissed(scope, undismiss, false)
    const fresh = items.filter((t) => !selectedIds.has(t.id))
    if (fresh.length) write([...current, ...fresh.map(termOf)])
  }
  const remove = (id) => {
    if (selectedIds.has(id)) write(current.filter((t) => (t.hpo_id || t.value) !== id))
  }
  const nodeOf = (id) => (graph ? graph.nodes.find((n) => n.id === id) : null)
  const graphActions = {
    accept: (id, name) => add([{ id, name: name || id, synonyms: nodeOf(id)?.synonyms || [] }]),
    // Dismissing an added term also takes it off the sample.
    reject: (id) => {
      setDismissed(scope, [id], true)
      remove(id)
    },
    remove,
    restore: (id) => setDismissed(scope, [id], false),
  }

  const openGraph = () => {
    snapshot.current = { hpo, dismissed: dismissedOf(scope) }
    setGraphOpen(true)
  }
  const saveGraph = () => {
    snapshot.current = null
    setGraphOpen(false)
  }
  const cancelGraph = () => {
    const snap = snapshot.current
    snapshot.current = null
    setGraphOpen(false)
    if (!snap) return
    if (dismissedOf(scope) !== snap.dismissed) putDismissed(scope, snap.dismissed)
    if (idsOf(hpo) !== idsOf(snap.hpo)) onChange(snap.hpo || [])
  }

  const listed = terms.length > 0 || !!graph || dismissedIds.length > 0
  const shown = listed || status === 'loading' || status === 'error'

  return (
    <>
      {shown && (
        <Box sx={{ mt: 1, ...sx }}>
          <Box aria-live="polite">
            {status === 'loading' && <Finding sx={{ mb: listed ? 0.5 : 0 }} />}
            {status === 'error' && <CouldNot onRetry={retry} sx={{ mb: listed ? 0.5 : 0 }} />}
          </Box>
          {listed && (
            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1.5 }}>
              <Muted component="span">Suggested HPO terms</Muted>
              {terms.length > 1 && <TextLink onClick={() => add(terms)}>Add all</TextLink>}
              {graph && <TextLink onClick={openGraph}>View graph</TextLink>}
              {dismissedIds.length > 0 && (
                <TextLink tone="muted" onClick={() => setDismissed(scope, dismissedIds, false)}>
                  Restore {dismissedIds.length} dismissed
                </TextLink>
              )}
            </Box>
          )}
          {terms.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75, maxHeight: 200, overflowY: 'auto' }}>
              {terms.map((t) => (
                <Chip
                  key={t.id}
                  size="small"
                  variant="outlined"
                  icon={<AddIcon />}
                  label={t.name}
                  title={`${t.name} (${t.id})`}
                  aria-label={`Add ${t.name}`}
                  onClick={() => add([t])}
                  onDelete={() => setDismissed(scope, [t.id], true)}
                  deleteIcon={<CloseIcon titleAccess={`Dismiss ${t.name}`} />}
                  sx={{
                    maxWidth: 280,
                    fontSize: 13,
                    borderStyle: 'dashed',
                    '& .MuiChip-icon': { fontSize: 16 },
                    '& .MuiChip-deleteIcon': { fontSize: 16 },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
      {graphOpen && (
        <ClustersDialog
          label={label}
          text={text}
          onTextChange={onTextChange}
          status={status}
          retry={retry}
          graph={graph}
          count={selectedIds.size}
          selectedIds={selectedIds}
          dismissed={dismissed}
          actions={graphActions}
          onCancel={cancelGraph}
          onSave={saveGraph}
        />
      )}
    </>
  )
}

export default HpoSuggestions
